/**
 * **AI V1 cutoff** — TS mirror of `packages/core-rust/src/ai/release.rs`.
 *
 * Owner's decision (2026-08-14): the **AI tutor** is not published in V1. It
 * sends the literal text the student writes to an external provider
 * (Anthropic, OpenAI, DeepSeek…, configurable per academy) while
 * `privacy.html` declared that no data is shared with third parties, and in
 * an academy the student can be a minor. Until informed consent exists —and
 * from the guardian when applicable— the feature must not be available.
 *
 * Extension (2026-08-15): the cutoff also covers the **commercial chatbot**
 * and **AI authoring**. They started off, but an `owner`/`org_admin` of a
 * client academy could turn them on from their settings: that door reopened
 * exactly the contradiction the tutor cutoff had closed. The claim "we
 * don't share data with third parties" cannot depend on
 * no academy ticking a box.
 *
 * This half is the CLIENT's, and does the same as the Rust one in the same
 * spot: the decision lives in **feature resolution**
 * (`resolveFeatureEnabled` / `resolveFeatureEffective` from `./features`),
 * which is the single funnel navigation, route guards and buttons pass
 * through. None of them has to remember anything. The only surface that does
 * NOT go through there is the chatbot's public widget (seen by people
 * without an account, and therefore without resolved features): that one
 * queries `aiChatbotReleased()` directly.
 *
 * **Fail-closed**: the switches are read at build time and any doubt turns
 * off — absent, blank or unparsable ⇒ disabled. Being build-time is
 * deliberate: an already-published store binary cannot change
 * its mind on the fly.
 *
 * **One switch per surface**, not a global one: re-enabling them has
 * different requirements and the repo already upholds "one capability, one flag".
 *
 * Round-trip path: the `VITE_*` switch when building the client and its
 * unprefixed twin on the server. Records:
 * `docs/auditoria/2026-08-14-declaraciones-veraces.md` (tutor) and
 * `docs/auditoria/2026-08-15-corte-ia-v1-completo.md` (chatbot + authoring).
 */

/** Tutor's build switch. Mirror of `ai::release::CLIENT_ENV_KEY`. */
export const AI_TUTOR_RELEASE_ENV = "VITE_AI_TUTOR_V1_RELEASE";
/** Chatbot's build switch. Mirror of `CHATBOT_CLIENT_ENV_KEY`. */
export const AI_CHATBOT_RELEASE_ENV = "VITE_AI_CHATBOT_V1_RELEASE";
/** Authoring's build switch. Mirror of `AUTHORING_CLIENT_ENV_KEY`. */
export const AI_AUTHORING_RELEASE_ENV = "VITE_AI_AUTHORING_V1_RELEASE";

/**
 * Table `feature key → switch that returns it`. EXACT mirror of
 * `ai::release::V1_FEATURE_SWITCHES` (there are tests on both sides that
 * compare it against the key registry and against the manifest).
 */
export const AI_V1_FEATURE_SWITCHES: readonly (readonly [string, string])[] = [
  ["academy.ai_tutor", AI_TUTOR_RELEASE_ENV],
  ["academy.ai_chatbot", AI_CHATBOT_RELEASE_ENV],
  ["academy.ai_authoring", AI_AUTHORING_RELEASE_ENV],
  ["academy.ai_authoring.edit", AI_AUTHORING_RELEASE_ENV],
  ["academy.ai_authoring.config", AI_AUTHORING_RELEASE_ENV],
  ["academy.ai_authoring.autoapply", AI_AUTHORING_RELEASE_ENV],
];

/**
 * Features the V1 cutoff turns off no matter what. Flat mirror of
 * `AI_V1_FEATURE_SWITCHES` and of `ai::release::V1_DISABLED_FEATURES`.
 */
export const AI_V1_DISABLED_FEATURES: readonly string[] = AI_V1_FEATURE_SWITCHES.map(
  ([key]) => key,
);

/**
 * **Fail-closed** parsing of the switch. Only `1|true|yes|on` turn it on;
 * `undefined`, `""`, `"  "`, `"maybe"` and anything else turn it off.
 */
export function parseAiReleaseFlag(raw: unknown): boolean {
  if (typeof raw !== "string") {
    return false;
  }
  switch (raw.trim().toLowerCase()) {
    case "1":
    case "true":
    case "yes":
    case "on": {
      return true;
    }
    default: {
      return false;
    }
  }
}

/**
 * Reads a switch from the build environment. Tolerant of contexts without
 * Vite (bun tests, native bundling): absence ⇒ off, which is exactly the
 * default we want.
 */
function readReleaseEnv(name: string): unknown {
  try {
    // Same pattern as `powersync/utils.ts::shouldForceMultiTabs`: `import.meta.env`
    // doesn't exist outside Vite (bun tests, native bundling).
    const viteEnv = (import.meta as { env?: Record<string, unknown> }).env;
    return viteEnv?.[name];
  } catch {
    // No `import.meta`: off, which is the default we want.
    return undefined;
  }
}

/** Is the AI tutor released in this client build? */
export function aiTutorReleased(): boolean {
  return parseAiReleaseFlag(readReleaseEnv(AI_TUTOR_RELEASE_ENV));
}

/** Is the academy chatbot released in this client build? */
export function aiChatbotReleased(): boolean {
  return parseAiReleaseFlag(readReleaseEnv(AI_CHATBOT_RELEASE_ENV));
}

/** Is AI authoring released in this client build? */
export function aiAuthoringReleased(): boolean {
  return parseAiReleaseFlag(readReleaseEnv(AI_AUTHORING_RELEASE_ENV));
}

/** Is `key` one of the features the V1 cutoff turns off? */
export function isAiV1DisabledFeature(key: string): boolean {
  return AI_V1_DISABLED_FEATURES.includes(key);
}

/** Switch that would return `key`, if the cutoff covers it. */
export function aiV1SwitchForFeature(key: string): string | undefined {
  return AI_V1_FEATURE_SWITCHES.find(([k]) => k === key)?.[1];
}

/**
 * Is the surface `key` belongs to released in V1? A key the cutoff doesn't
 * cover is "released" by definition.
 */
export function isAiFeatureReleased(key: string): boolean {
  const env = aiV1SwitchForFeature(key);
  return env === undefined ? true : parseAiReleaseFlag(readReleaseEnv(env));
}

/**
 * Clamp on feature resolution: the last word, after
 * course → org → platform → manifest default. No level can turn on
 * what V1 doesn't release.
 */
export function clampFeatureToAiV1Release(key: string, enabled: boolean): boolean {
  if (enabled && !isAiFeatureReleased(key)) {
    return false;
  }
  return enabled;
}
